// src/booking/BookingPage.jsx
//
// Public booking flow, reached at /book/:slug.
//
// Stage B + Layer 1 (Session 10): a client can pick one or many standalone
// services AND/OR one or many packages. Packages expand into constituent
// services client-side for duration and allowed-days calcs; the backend
// re-expands at submit and is authoritative on deposits, patch tests, and
// day restrictions.
//
// Package selection shape:
//   selectedPackages = [
//     { package_id: 5, slot_selections: { [slot_id]: service_id, ... } }
//   ]
// Fixed slots auto-populate slot_selections with their one option so the
// "is this package complete?" check is a simple "every slot has a pick."
//
// Deposit policy (mirrors backend _calc_deposit, semantics agreed
// Session 10 as (a.ii)):
//   - Packages with deposit_override pay that flat amount.
//   - Standalones + non-override packages form one bucket.
//   - The account rule (none/fixed/percent) applies ONCE to that bucket.
//   - Final deposit = sum of overrides + rule-on-bucket.
// If backend _calc_deposit changes, this must change in lockstep.
//
// Day-location V1 (Aug 2026): the /page/:slug response now includes
// day_locations (see routes/booking_routes.py). DateTimeStep shows the
// day's location label under each date chip, using the same mon-first
// DAY_KEYS/index mapping already used for allowed-days calcs — so a
// client picking a date can see which salon that day is at before they
// commit. Purely a display label; no effect on availability logic.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import './BookingPage.css';
import RetailRecommendations from '../solo/RetailRecommendations';

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_CONNECT_PUBLISHABLE_KEY || ''
);

const API = (path) => `/api/booking${path}`;

const formatPrice = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
    .format(Number(n || 0));

const weekdayShort = (date) =>
  date.toLocaleDateString('en-GB', { weekday: 'short' });

const toLocalISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const totalDurationMins = (services) =>
  services.reduce(
    (sum, s) => sum + (s.duration || 0) + (s.processing_time || 0),
    0
  );

const totalPrice = (services) =>
  services.reduce((sum, s) => sum + Number(s.price || 0), 0);

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Same mon-first weekday index used by allowedDaysIntersection/isDateAllowed
// below — kept as a shared helper so the location lookup and the
// allowed-days lookup can never drift apart from each other.
const dayKeyForDate = (date) => DAY_KEYS[(date.getDay() + 6) % 7];

const parseAllowedDays = (raw) => {
  if (!raw) return null;
  const days = String(raw)
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  return days.length ? new Set(days) : null;
};

const allowedDaysIntersection = (services) => {
  let intersection = null;
  for (const s of services) {
    const days = parseAllowedDays(s.allowed_days);
    if (days === null) continue;
    if (intersection === null) {
      intersection = new Set(days);
    } else {
      intersection = new Set([...intersection].filter((d) => days.has(d)));
    }
  }
  return intersection;
};

const isDateAllowed = (date, intersection) => {
  if (intersection === null) return true;
  const idx = (date.getDay() + 6) % 7;
  return intersection.has(DAY_KEYS[idx]);
};

const formatDuration = (mins) => {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

// ---------- Package helpers ----------

// Find the option (service) a client has picked for a given slot.
const getSlotSelection = (slot, selection) => {
  if (!slot.options.length) return null;
  const picked = selection?.slot_selections?.[slot.id];
  if (picked != null) {
    return slot.options.find((o) => o.service_id === picked) || null;
  }
  // Fixed slots default to their single option.
  if (slot.slot_type === 'fixed') return slot.options[0];
  return null;
};

const isPackageComplete = (pkg, selection) => {
  if (!selection) return false;
  return pkg.slots.every((slot) => getSlotSelection(slot, selection) != null);
};

// Flatten all selected packages into a service list (same shape as standalone
// service objects). Preserves package order, then slot order within each.
const expandPackageServices = (packages, selectedPackages) => {
  const out = [];
  for (const sel of selectedPackages) {
    const pkg = packages.find((p) => p.id === sel.package_id);
    if (!pkg) continue;
    for (const slot of pkg.slots) {
      const opt = getSlotSelection(slot, sel);
      if (!opt) continue;
      out.push({
        id: opt.service_id,
        name: opt.service_name,
        duration: opt.duration,
        processing_time: opt.processing_time,
        price: opt.price,
        requires_patch_test: opt.requires_patch_test,
        allowed_days: opt.allowed_days,
      });
    }
  }
  return out;
};

// Compute a package's effective price given the client's current picks.
// Mirrors the backend expansion helper: override wins; otherwise sum of
// picked options. When a choice slot hasn't been picked yet, uses the
// cheapest option as a preview ("from" price).
const packageEffectivePrice = (pkg, selection) => {
  if (pkg.price_override != null) return pkg.price_override;
  let total = 0;
  for (const slot of pkg.slots) {
    const picked = getSlotSelection(slot, selection);
    if (picked) {
      total += Number(picked.price || 0);
    } else {
      total += Math.min(...slot.options.map((o) => Number(o.price || 0)));
    }
  }
  return Math.round(total * 100) / 100;
};

// Deposit calc mirroring backend _calc_deposit (a.ii semantics).
// standaloneServices: array of service objects with .price
// packages: the full page.packages array
// selectedPackages: client's picks
const calcDeposit = (page, standaloneServices, packages, selectedPackages) => {
  if (!page || page.deposit_type === 'none') return 0;

  // 1) Override deposits — flat amounts.
  let overrideTotal = 0;
  // 2) Non-override package effective prices — go into the rule bucket.
  let nonOverrideTotal = 0;

  for (const sel of selectedPackages) {
    const pkg = packages.find((p) => p.id === sel.package_id);
    if (!pkg) continue;
    if (pkg.deposit_override != null) {
      overrideTotal += Number(pkg.deposit_override);
    } else {
      nonOverrideTotal += packageEffectivePrice(pkg, sel);
    }
  }

  const standaloneTotal = standaloneServices.reduce(
    (s, sv) => s + Number(sv.price || 0),
    0
  );
  const ruleBase = standaloneTotal + nonOverrideTotal;

  let ruleAmount = 0;
  if (ruleBase > 0) {
    if (page.deposit_type === 'fixed') {
      ruleAmount = Number(page.deposit_value || 0);
    } else {
      ruleAmount =
        Math.round(ruleBase * (page.deposit_value / 100) * 100) / 100;
    }
  }

  return Math.round((overrideTotal + ruleAmount) * 100) / 100;
};

// ---------- Step components ----------

function ServiceStep({
  services,
  packages,
  selectedServices,
  selectedPackages,
  onToggleService,
  onTogglePackage,
  onSlotPick,
}) {
  const hasAny = (services?.length || 0) + (packages?.length || 0) > 0;
  if (!hasAny) {
    return <p className="bp-empty">No services available yet.</p>;
  }

  const isServiceSelected = (s) => selectedServices.some((x) => x.id === s.id);
  const getSelection = (pkg) =>
    selectedPackages.find((p) => p.package_id === pkg.id);

  return (
    <div className="bp-service-list">
      {packages.map((pkg) => {
        const selection = getSelection(pkg);
        const selected = !!selection;
        const priceValue = selected
          ? packageEffectivePrice(pkg, selection)
          : pkg.effective_price;
        const priceLabel =
          pkg.price_is_from && !selected
            ? `from ${formatPrice(priceValue)}`
            : formatPrice(priceValue);

        return (
          <div
            key={`pkg-${pkg.id}`}
            className={`bp-service-card bp-package-card ${selected ? 'selected' : ''}`}
          >
            <button
              type="button"
              className="bp-package-header"
              onClick={() => onTogglePackage(pkg)}
              aria-pressed={selected}
            >
              <div className="bp-service-row">
                <span className="bp-service-name">
                  {selected ? '✓ ' : ''}
                  {pkg.name}
                  <span className="bp-package-tag">Package</span>
                </span>
                <span className="bp-service-price">{priceLabel}</span>
              </div>
              {pkg.description && (
                <div className="bp-package-desc">{pkg.description}</div>
              )}
            </button>

            {selected && (
              <div className="bp-package-slots">
                {pkg.slots.map((slot) => {
                  if (slot.slot_type === 'fixed') {
                    const opt = slot.options[0];
                    return (
                      <div key={slot.id} className="bp-slot-fixed">
                        <span className="bp-slot-label">
                          {slot.display_label}:
                        </span>{' '}
                        <span>{opt.service_name}</span>
                        {opt.requires_patch_test && (
                          <span className="bp-badge-patch">
                            Patch test required
                          </span>
                        )}
                      </div>
                    );
                  }
                  // choice slot
                  const picked = selection.slot_selections[slot.id];
                  return (
                    <fieldset key={slot.id} className="bp-slot-choice">
                      <legend className="bp-slot-label">
                        {slot.display_label}
                      </legend>
                      {slot.options.map((opt) => {
                        const id = `slot-${slot.id}-opt-${opt.service_id}`;
                        const checked = picked === opt.service_id;
                        return (
                          <label
                            key={opt.service_id}
                            htmlFor={id}
                            className={`bp-slot-option ${checked ? 'selected' : ''}`}
                          >
                            <input
                              type="radio"
                              id={id}
                              name={`slot-${slot.id}`}
                              checked={checked}
                              onChange={() =>
                                onSlotPick(pkg.id, slot.id, opt.service_id)
                              }
                            />
                            <span className="bp-slot-option-name">
                              {opt.service_name}
                              {opt.requires_patch_test && (
                                <span className="bp-badge-patch">
                                  Patch test required
                                </span>
                              )}
                            </span>
                            <span className="bp-slot-option-price">
                              {formatPrice(opt.price)}
                            </span>
                          </label>
                        );
                      })}
                    </fieldset>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {services.map((s) => {
        const sel = isServiceSelected(s);
        return (
          <button
            key={`svc-${s.id}`}
            type="button"
            className={`bp-service-card ${sel ? 'selected' : ''}`}
            onClick={() => onToggleService(s)}
            aria-pressed={sel}
          >
            <div className="bp-service-row">
              <span className="bp-service-name">
                {sel ? '✓ ' : ''}
                {s.name}
              </span>
              <span className="bp-service-price">{formatPrice(s.price)}</span>
            </div>
            <div className="bp-service-meta">
              <span>
                {s.duration} min
                {s.processing_time
                  ? ` + ${s.processing_time} min processing`
                  : ''}
              </span>
              {s.requires_patch_test && (
                <span className="bp-badge-patch">Patch test required</span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// Summary used on steps 3, 4, 5. Packages render as single lines with
// constituent services indented underneath for clarity.
function BookingSummary({
  standaloneServices,
  packages,
  selectedPackages,
  deposit,
}) {
  const totalDur = totalDurationMins([
    ...standaloneServices,
    ...expandPackageServices(packages, selectedPackages),
  ]);
  const totalPackagePrice = selectedPackages.reduce((sum, sel) => {
    const pkg = packages.find((p) => p.id === sel.package_id);
    return pkg ? sum + packageEffectivePrice(pkg, sel) : sum;
  }, 0);
  const grandTotal = totalPackagePrice + totalPrice(standaloneServices);
  const showTotal =
    standaloneServices.length + selectedPackages.length > 1 ||
    (selectedPackages.length === 1 && standaloneServices.length >= 1);

  return (
    <div className="bp-services-summary" style={{ marginBottom: 12 }}>
      {selectedPackages.map((sel) => {
        const pkg = packages.find((p) => p.id === sel.package_id);
        if (!pkg) return null;
        const items = expandPackageServices(packages, [sel]);
        return (
          <div key={`pkg-${sel.package_id}`} className="bp-summary-package">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 12,
                fontWeight: 600,
              }}
            >
              <span>{pkg.name}</span>
              <span>{formatPrice(packageEffectivePrice(pkg, sel))}</span>
            </div>
            {items.map((svc, i) => (
              <div
                key={`${sel.package_id}-${svc.id}-${i}`}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  paddingLeft: 16,
                  opacity: 0.7,
                  fontSize: '0.9em',
                }}
              >
                <span>· {svc.name}</span>
              </div>
            ))}
          </div>
        );
      })}

      {standaloneServices.map((s, i) => (
        <div
          key={`svc-${s.id}-${i}`}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
        >
          <span>{s.name}</span>
          <span>{formatPrice(s.price)}</span>
        </div>
      ))}

      {showTotal && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(0,0,0,0.08)',
            marginTop: 6,
            paddingTop: 6,
            fontWeight: 600,
          }}
        >
          <span>Total</span>
          <span>{formatPrice(grandTotal)}</span>
        </div>
      )}

      <div style={{ opacity: 0.7, fontSize: '0.9em', marginTop: 4 }}>
        {formatDuration(totalDur)} total
      </div>

      {deposit > 0 && (
        <div style={{ marginTop: 6 }}>
          Deposit today: {formatPrice(deposit)}
        </div>
      )}
    </div>
  );
}

function DateTimeStep({
  slug,
  expandedServices,
  allowedIntersection,
  advanceDays,
  dayLocations,
  onPick,
}) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refire when the expanded service list changes — not just the standalone
  // ids, because swapping a package's choice can change durations/allowed
  // days even when the same package stays selected.
  const serviceIdsKey = useMemo(
    () => expandedServices.map((s) => s.id).join(','),
    [expandedServices]
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateRange = useMemo(() => {
    const dates = [];
    for (let i = 0; i < Math.min(advanceDays || 60, 28); i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }
    return dates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceDays]);

  useEffect(() => {
    if (!selectedDate || !serviceIdsKey) return;
    setLoading(true);
    const iso = toLocalISODate(selectedDate);
    fetch(
      API(`/availability/${slug}?date=${iso}&service_ids=${serviceIdsKey}`)
    )
      .then((r) => r.json())
      .then((data) => setSlots(data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [selectedDate, serviceIdsKey, slug]);

  return (
    <div>
      <label className="bp-label">Pick a date</label>
      <div className="bp-date-row">
        {dateRange.map((d) => {
          const iso = toLocalISODate(d);
          const isSel = selectedDate && toLocalISODate(selectedDate) === iso;
          const allowed = isDateAllowed(d, allowedIntersection);
          const location = dayLocations?.[dayKeyForDate(d)];
          return (
            <button
              key={iso}
              type="button"
              className={`bp-date-chip ${isSel ? 'selected' : ''} ${
                allowed ? '' : 'disabled'
              }`}
              onClick={() => allowed && setSelectedDate(d)}
              disabled={!allowed}
              title={
                allowed ? undefined : 'Not available for the selected services'
              }
              aria-disabled={!allowed}
            >
              <span className="bp-date-wd">{weekdayShort(d)}</span>
              <span className="bp-date-num">{d.getDate()}</span>
              {location && (
                <span className="bp-date-loc">{location}</span>
              )}
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <>
          <label className="bp-label" style={{ marginTop: 20 }}>
            Pick a time
          </label>
          {loading ? (
            <p className="bp-muted">Checking availability…</p>
          ) : slots.length === 0 ? (
            <p className="bp-muted">No times available on this day.</p>
          ) : (
            <div className="bp-slot-grid">
              {slots.map((t) => (
                <button
                  key={t}
                  type="button"
                  className="bp-slot"
                  onClick={() => onPick(toLocalISODate(selectedDate), t)}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DetailsStep({ slug, expandedServices, onNext, onBack }) {
  const [form, setForm] = useState({
    client_name: '',
    client_phone: '',
    client_email: '',
  });
  const [error, setError] = useState('');
  const [ptCheck, setPtCheck] = useState(null);
  const [ptLoading, setPtLoading] = useState(false);

  const anyNeedsPatchTest = expandedServices.some((s) => s.requires_patch_test);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === 'client_phone' && ptCheck) {
      setPtCheck(null);
    }
  };

  const runPatchTestCheck = async () => {
    if (!anyNeedsPatchTest) return;
    const phone = form.client_phone.trim();
    if (phone.replace(/\D/g, '').length < 7) return;

    setPtLoading(true);
    try {
      const res = await fetch(API('/patch-test-check'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          client_phone: phone,
          service_ids: expandedServices.map((s) => s.id),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setPtCheck(data);
      }
    } catch {
      // fail open — submit endpoints enforce
    } finally {
      setPtLoading(false);
    }
  };

  const submit = () => {
    if (!form.client_name.trim()) return setError('Please enter your name');
    if (!form.client_phone.trim())
      return setError('Please enter a phone number');
    if (ptCheck?.action === 'block') {
      return setError(
        "This booking can't be completed online. Please contact the stylist directly."
      );
    }
    setError('');
    onNext(form, ptCheck);
  };

  const blocked = ptCheck?.action === 'block';
  const provisional = ptCheck?.action === 'provisional';

  const patchServiceNames = expandedServices
    .filter((s) => s.requires_patch_test)
    .map((s) => s.name);

  return (
    <div className="bp-form">
      <label className="bp-label">Your name</label>
      <input
        className="bp-input"
        name="client_name"
        value={form.client_name}
        onChange={handleChange}
        autoComplete="name"
      />

      <label className="bp-label">Mobile number</label>
      <input
        className="bp-input"
        name="client_phone"
        value={form.client_phone}
        onChange={handleChange}
        onBlur={runPatchTestCheck}
        autoComplete="tel"
        inputMode="tel"
      />
      {ptLoading && <p className="bp-muted">Checking…</p>}

      <label className="bp-label">Email (optional)</label>
      <input
        className="bp-input"
        name="client_email"
        value={form.client_email}
        onChange={handleChange}
        autoComplete="email"
        type="email"
      />

      {blocked && (
        <div className="bp-block">
          <strong>We can't complete this booking online</strong>
          <p>{ptCheck.message}</p>
        </div>
      )}

      {provisional && (
        <div className="bp-warn">
          <strong>Patch test needed</strong>
          <p>
            {patchServiceNames.length > 1 ? (
              <>
                {patchServiceNames.join(' and ')} need a patch test at least 48
                hours before your appointment.
              </>
            ) : patchServiceNames.length === 1 ? (
              <>
                {patchServiceNames[0]} needs a patch test at least 48 hours
                before your appointment.
              </>
            ) : (
              <>
                This booking needs a patch test at least 48 hours before your
                appointment.
              </>
            )}{' '}
            Continue to confirm your booking — then call or pop in soon to
            arrange your patch test. If no test has been done by 48 hours
            before your appointment, the booking will be automatically
            cancelled.
          </p>
        </div>
      )}

      {error && <p className="bp-error">{error}</p>}

      <div className="bp-actions">
        <button type="button" className="bp-btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="bp-btn-primary"
          onClick={submit}
          disabled={blocked || ptLoading}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// ---------- Inner Stripe payment form ----------

function PaymentInner({ clientSecret, onSuccess, amount, provisional }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');
    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      onSuccess();
      return;
    }
    setSubmitting(false);
  };

  return (
    <div className="bp-payment">
      <PaymentElement />
      {error && <p className="bp-error">{error}</p>}
      {provisional && (
        <p className="bp-muted bp-deposit-note">
          Your booking will be confirmed once payment is complete. If no patch
          test has been done by 48 hours before your appointment, the booking
          will be automatically cancelled and the deposit is non-refundable.
        </p>
      )}
      <button
        type="button"
        className="bp-btn-primary bp-btn-pay"
        onClick={handlePay}
        disabled={!stripe || submitting}
      >
        {submitting ? 'Processing…' : `Pay ${formatPrice(amount)} deposit`}
      </button>
    </div>
  );
}

// ---------- Main page ----------

export default function BookingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [details, setDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [depositAmount, setDepositAmount] = useState(0);
  const [connectAccount, setConnectAccount] = useState(null);
  const [confirmed, setConfirmed] = useState(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [provisional, setProvisional] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [provisionalMessage, setProvisionalMessage] = useState('');

  useEffect(() => {
    fetch(API(`/page/${slug}`))
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((data) => {
        setPage({
          ...data,
          services: data.services || [],
          packages: data.packages || [],
          day_locations: data.day_locations || {},
        });
      })
      .catch(() =>
        setLoadError("This booking page isn't available right now.")
      );
  }, [slug]);

  const packages = page?.packages || [];
  const services = page?.services || [];

  const packageExpandedServices = useMemo(
    () => expandPackageServices(packages, selectedPackages),
    [packages, selectedPackages]
  );
  const expandedServices = useMemo(
    () => [...packageExpandedServices, ...selectedServices],
    [packageExpandedServices, selectedServices]
  );
  const allowedIntersection = allowedDaysIntersection(expandedServices);

  // If selection changes such that the chosen date is no longer valid,
  // clear it so the client re-picks on step 2.
  useEffect(() => {
    if (!date) return;
    const d = new Date(date + 'T00:00:00');
    if (!isDateAllowed(d, allowedIntersection)) {
      setDate(null);
      setTime(null);
    }
  }, [expandedServices, date, allowedIntersection]);

  const toggleService = (svc) => {
    setSelectedServices((curr) => {
      const exists = curr.some((s) => s.id === svc.id);
      return exists ? curr.filter((s) => s.id !== svc.id) : [...curr, svc];
    });
  };

  const togglePackage = (pkg) => {
    setSelectedPackages((curr) => {
      const idx = curr.findIndex((p) => p.package_id === pkg.id);
      if (idx !== -1) {
        return curr.filter((_, i) => i !== idx);
      }
      // Pre-populate fixed slots; choice slots stay empty.
      const slot_selections = {};
      for (const slot of pkg.slots) {
        if (slot.slot_type === 'fixed') {
          slot_selections[slot.id] = slot.options[0].service_id;
        }
      }
      return [...curr, { package_id: pkg.id, slot_selections }];
    });
  };

  const pickSlotOption = (pkgId, slotId, serviceId) => {
    setSelectedPackages((curr) =>
      curr.map((sel) =>
        sel.package_id === pkgId
          ? {
              ...sel,
              slot_selections: {
                ...sel.slot_selections,
                [slotId]: serviceId,
              },
            }
          : sel
      )
    );
  };

  // Continue button on step 1 enabled only when:
  // - at least one thing is selected, AND
  // - every selected package has all its choice slots filled, AND
  // - allowed_days intersection is non-empty
  const allPackagesComplete = selectedPackages.every((sel) => {
    const pkg = packages.find((p) => p.id === sel.package_id);
    return pkg ? isPackageComplete(pkg, sel) : false;
  });
  const hasSelection = selectedServices.length + selectedPackages.length > 0;
  const noSharedDays =
    allowedIntersection !== null && allowedIntersection.size === 0;
  const canContinueFromStep1 =
    hasSelection && allPackagesComplete && !noSharedDays;

  const deposit = page
    ? calcDeposit(page, selectedServices, packages, selectedPackages)
    : 0;

  const goToPayment = async (formData, ptCheck) => {
    setDetails(formData);
    setError('');
    const frontendProvisional = ptCheck?.action === 'provisional';
    setProvisional(frontendProvisional);
    setProvisionalMessage(frontendProvisional ? ptCheck.message || '' : '');

    const payload = {
      slug,
      service_ids: selectedServices.map((s) => s.id),
      packages: selectedPackages.map((sel) => ({
        package_id: sel.package_id,
        slot_selections: sel.slot_selections,
      })),
      date,
      time,
      ...formData,
    };

    if (page.deposit_type === 'none') {
      setWorking(true);
      try {
        const res = await fetch(API('/no-deposit'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.blocked) {
            setError(
              data.error ||
                "This booking can't be completed online. Please contact the stylist."
            );
            return;
          }
          throw new Error(data.error || 'Booking failed');
        }
        setProvisional(!!data.provisional);
        setProvisionalMessage(data.patch_test_message || '');
        setConfirmed({
          services: selectedServices,
          packages: selectedPackages,
          date,
          time,
          stylist: page.business_name,
        });
        setStep(5);
      } catch (e) {
        setError(e.message);
      } finally {
        setWorking(false);
      }
      return;
    }

    setWorking(true);
    try {
      const res = await fetch(API('/create-intent'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.blocked) {
          setError(
            data.error ||
              "This booking can't be completed online. Please contact the stylist."
          );
          return;
        }
        throw new Error(data.error || 'Payment setup failed');
      }
      setClientSecret(data.client_secret);
      setDepositAmount(data.amount);
      setConnectAccount(data.stripe_account_id);
      setProvisional(!!data.provisional);
      setProvisionalMessage(data.patch_test_message || '');
      setStep(4);
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  const handlePaymentSuccess = () => {
    setConfirmed({
      services: selectedServices,
      packages: selectedPackages,
      date,
      time,
      stylist: page.business_name,
    });
    setStep(5);
  };

  if (loadError) {
    return (
      <div className="bp-shell">
        <div className="bp-card">
          <h2>Not available</h2>
          <p>{loadError}</p>
        </div>
      </div>
    );
  }
  if (!page) {
    return (
      <div className="bp-shell">
        <p className="bp-muted">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bp-shell">
      <div className="bp-card">
        <header className="bp-header">
          <h1>{page.business_name || 'Book an appointment'}</h1>
          {page.bio && <p className="bp-bio">{page.bio}</p>}
        </header>

        {step < 5 && (
          <div className="bp-steps">
            {[
              'Service',
              'Date & time',
              'Your details',
              page.deposit_type === 'none' ? 'Confirm' : 'Payment',
            ].map((label, i) => (
              <div
                key={label}
                className={`bp-step-pill ${step > i + 1 ? 'done' : ''} ${
                  step === i + 1 ? 'current' : ''
                }`}
              >
                <span className="bp-step-num">{i + 1}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <>
            <ServiceStep
              services={services}
              packages={packages}
              selectedServices={selectedServices}
              selectedPackages={selectedPackages}
              onToggleService={toggleService}
              onTogglePackage={togglePackage}
              onSlotPick={pickSlotOption}
            />
            {hasSelection && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 8,
                }}
                aria-live="polite"
              >
                <span>
                  {(() => {
                    const parts = [];
                    if (selectedPackages.length)
                      parts.push(
                        `${selectedPackages.length} package${selectedPackages.length > 1 ? 's' : ''}`
                      );
                    if (selectedServices.length)
                      parts.push(
                        `${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''}`
                      );
                    return parts.join(' + ');
                  })()}
                  {expandedServices.length > 0 && (
                    <> · {formatDuration(totalDurationMins(expandedServices))}</>
                  )}
                </span>
              </div>
            )}
            {!allPackagesComplete && selectedPackages.length > 0 && (
              <p className="bp-muted" style={{ marginTop: 8 }}>
                Please choose an option in each section to continue.
              </p>
            )}
            {noSharedDays && (
              <div className="bp-warn" style={{ marginTop: 12 }}>
                These services aren't offered on the same days — please pick a
                different combination.
              </div>
            )}
            <div className="bp-actions">
              <span />
              <button
                type="button"
                className="bp-btn-primary"
                onClick={() => setStep(2)}
                disabled={!canContinueFromStep1}
              >
                Continue
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <DateTimeStep
              slug={slug}
              expandedServices={expandedServices}
              allowedIntersection={allowedIntersection}
              advanceDays={page.advance_booking_days}
              dayLocations={page.day_locations}
              onPick={(d, t) => {
                setDate(d);
                setTime(t);
                setStep(3);
              }}
            />
            <div className="bp-actions">
              <button
                type="button"
                className="bp-btn-secondary"
                onClick={() => setStep(1)}
              >
                Back
              </button>
              <span />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="bp-summary">
              <BookingSummary
                standaloneServices={selectedServices}
                packages={packages}
                selectedPackages={selectedPackages}
                deposit={deposit}
              />
              <div>
                {new Date(date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}{' '}
                at {time}
              </div>
            </div>
            <DetailsStep
              slug={slug}
              expandedServices={expandedServices}
              onBack={() => setStep(2)}
              onNext={goToPayment}
            />
            {working && <p className="bp-muted">Setting up payment…</p>}
            {error && <p className="bp-error">{error}</p>}
          </>
        )}

        {step === 4 && clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: 'stripe' },
              stripeAccount: connectAccount || undefined,
            }}
          >
            <div className="bp-summary">
              <BookingSummary
                standaloneServices={selectedServices}
                packages={packages}
                selectedPackages={selectedPackages}
                deposit={0}
              />
              <div>
                {new Date(date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}{' '}
                at {time}
              </div>
              <div>Deposit: {formatPrice(depositAmount)}</div>
            </div>
            {provisional && (
              <div className="bp-warn">
                <strong>Patch test needed</strong>
                <p>
                  This booking needs a patch test at least 48 hours before your
                  appointment. Pay your deposit to confirm your booking, then
                  call or pop in soon to arrange your test.
                </p>
              </div>
            )}
            <PaymentInner
              clientSecret={clientSecret}
              amount={depositAmount}
              provisional={provisional}
              onSuccess={handlePaymentSuccess}
            />
            <div className="bp-actions">
              <button
                type="button"
                className="bp-btn-secondary"
                onClick={() => {
                  setClientSecret(null);
                  setStep(3);
                }}
              >
                Back
              </button>
              <span />
            </div>
          </Elements>
        )}

{step === 5 && confirmed && (
  <div className="bp-confirmed">
    <div className={`bp-tick ${provisional ? 'provisional' : ''}`}>
      {provisional ? '⚠' : '✓'}
    </div>
    <h2>
      {provisional
        ? 'Booking confirmed — patch test needed'
        : 'Booking confirmed'}
    </h2>
    <div className="bp-summary">
      <BookingSummary
        standaloneServices={confirmed.services}
        packages={packages}
        selectedPackages={confirmed.packages}
        deposit={0}
      />
      <div>
        {new Date(confirmed.date).toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}{' '}
        at {confirmed.time}
      </div>
      <div>with {confirmed.stylist}</div>
    </div>

    <RetailRecommendations
      serviceIds={[
        ...expandPackageServices(packages, confirmed.packages).map((s) => s.id),
        ...confirmed.services.map((s) => s.id),
      ]}
      publicSlug={slug}
    />

    {provisional ? (
      <div className="bp-warn" style={{ marginTop: 16 }}>
        <strong>Your booking is confirmed — patch test needed</strong>
        <p>
          This booking needs a patch test at least 48 hours before your
          appointment. Please call or pop in soon to arrange one. If no
          patch test has been done by 48 hours before your appointment,
          the booking will be automatically cancelled.
        </p>
      </div>
    ) : (
          <p className="bp-muted">
            You'll receive a confirmation SMS shortly.
          </p>
        )}
      </div>
    )}
      </div>
    </div>
  );
}